import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { SafeAreaView } from '../layout/SafeAreaView';
import { TopBar } from '../layout/TopBar';
import {
  getUserShoppingLists,
  createShoppingList,
  addItemToList,
  toggleItemChecked,
  removeItemFromList,
  deleteShoppingList,
  updateShoppingList,
} from '../services/shoppingListService';
import { ShoppingList } from '../types/shoppingList';

export const ShoppingListsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [openNewList, setOpenNewList] = useState(false);
  const [openAddItem, setOpenAddItem] = useState(false);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [newListTitle, setNewListTitle] = useState('');
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, unit: 'unidade' });

  useEffect(() => {
    loadLists();
  }, [user?.uid]);

  const loadLists = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      const data = await getUserShoppingLists(user.uid);
      setLists(data);
    } catch (error) {
      console.error('Erro ao carregar listas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async () => {
    if (!user?.uid || !newListTitle.trim()) return;

    try {
      await createShoppingList(user.uid, newListTitle);
      setNewListTitle('');
      setOpenNewList(false);
      await loadLists();
    } catch (error) {
      console.error('Erro ao criar lista:', error);
    }
  };

  const handleAddItem = async () => {
    if (!selectedListId || !newItem.name.trim()) return;

    try {
      await addItemToList(selectedListId, {
        name: newItem.name,
        quantity: newItem.quantity,
        unit: newItem.unit,
        checked: false,
      });
      setNewItem({ name: '', quantity: 1, unit: 'unidade' });
      setOpenAddItem(false);
      await loadLists();
    } catch (error) {
      console.error('Erro ao adicionar item:', error);
    }
  };

  const handleToggleItem = async (listId: string, itemId: string) => {
    try {
      await toggleItemChecked(listId, itemId);
      await loadLists();
    } catch (error) {
      console.error('Erro ao marcar item:', error);
    }
  };

  const handleDeleteItem = async (listId: string, itemId: string) => {
    try {
      await removeItemFromList(listId, itemId);
      await loadLists();
    } catch (error) {
      console.error('Erro ao remover item:', error);
    }
  };

  const handleDeleteList = async (listId: string) => {
    if (!confirm('Deseja excluir esta lista?')) return;

    try {
      await deleteShoppingList(listId);
      await loadLists();
    } catch (error) {
      console.error('Erro ao excluir lista:', error);
    }
  };

  const handleCompleteList = async (listId: string) => {
    try {
      await updateShoppingList(listId, { status: 'completed' });
      await loadLists();
    } catch (error) {
      console.error('Erro ao completar lista:', error);
    }
  };

  return (
    <SafeAreaView>
      <TopBar title="Listas de Compras" showBackButton />
      <Box sx={{ p: 2, pb: 8 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenNewList(true)}
          sx={{
            backgroundColor: theme.colors.secondary,
            color: theme.colors.text.primary,
            mb: 3,
            '&:hover': {
              backgroundColor: theme.colors.secondary,
              opacity: 0.9,
            },
          }}
        >
          Nova Lista de Compras
        </Button>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: theme.colors.primary }} />
          </Box>
        ) : lists.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ShoppingCartIcon sx={{ fontSize: 80, color: theme.colors.text.secondary, opacity: 0.3, mb: 2 }} />
            <Typography sx={{ color: theme.colors.text.secondary }}>
              Nenhuma lista criada ainda
            </Typography>
          </Box>
        ) : (
          lists.map((list) => {
            const completedItems = list.items.filter(item => item.checked).length;
            const totalItems = list.items.length;
            const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

            return (
              <Card
                key={list.id}
                sx={{
                  mb: 2,
                  backgroundColor: theme.colors.card,
                  borderRadius: theme.radii.medium,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        {list.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip
                          size="small"
                          label={`${completedItems}/${totalItems} itens`}
                          sx={{
                            backgroundColor: theme.colors.background,
                            color: theme.colors.text.primary,
                          }}
                        />
                        <Chip
                          size="small"
                          label={`${Math.round(progress)}% completo`}
                          sx={{
                            backgroundColor: theme.colors.primary,
                            color: 'white',
                          }}
                        />
                      </Box>
                    </Box>
                    <IconButton onClick={() => handleDeleteList(list.id)} size="small">
                      <DeleteIcon sx={{ color: theme.colors.text.secondary }} />
                    </IconButton>
                  </Box>

                  <List dense>
                    {list.items.map((item) => (
                      <ListItem
                        key={item.id}
                        disablePadding
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          backgroundColor: item.checked ? `${theme.colors.primary}15` : 'transparent',
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteItem(list.id, item.id)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={item.checked}
                            onChange={() => handleToggleItem(list.id, item.id)}
                            sx={{ color: theme.colors.primary }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography
                                sx={{
                                  textDecoration: item.checked ? 'line-through' : 'none',
                                  color: theme.colors.text.primary,
                                }}
                              >
                                {item.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: theme.colors.text.secondary }}
                              >
                                ({item.quantity} {item.unit})
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setSelectedListId(list.id);
                        setOpenAddItem(true);
                      }}
                      sx={{ borderColor: theme.colors.primary, color: theme.colors.primary }}
                    >
                      Adicionar Item
                    </Button>
                    {list.status === 'active' && totalItems > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleCompleteList(list.id)}
                        sx={{
                          backgroundColor: theme.colors.primary,
                          color: 'white',
                          '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 },
                        }}
                      >
                        Concluir
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>

      <Dialog open={openNewList} onClose={() => setOpenNewList(false)}>
        <DialogTitle sx={{ color: theme.colors.text.primary }}>Nova Lista</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome da lista"
            value={newListTitle}
            onChange={(e) => setNewListTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewList(false)}>Cancelar</Button>
          <Button onClick={handleCreateList} variant="contained">
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddItem} onClose={() => setOpenAddItem(false)}>
        <DialogTitle sx={{ color: theme.colors.text.primary }}>Adicionar Item</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome do item"
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              type="number"
              label="Quantidade"
              value={newItem.quantity}
              onChange={(e) => setNewItem({ ...newItem, quantity: Number(e.target.value) })}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Unidade"
              value={newItem.unit}
              onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
              sx={{ flex: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddItem(false)}>Cancelar</Button>
          <Button onClick={handleAddItem} variant="contained">
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </SafeAreaView>
  );
};
